import React, { useMemo } from 'react';
import {
    Offer,
    PaymentPlace,
    TarifficatorPurchaseSuccessWidgetEvent,
    useAnalytics,
    useAuthManager,
} from '@funtech-int/yango-shared';

import { Button } from '@/shared/components/Button/Button';
import { useLoginUrl } from '@/shared/hooks/useLoginUrl';
import { useOfferWebAnalytics } from '@/shared/hooks/useOfferWebAnalytics';
import { useSubscriptionRefetch } from '@/shared/hooks/useSubscriptionRefetch';
import { getAutoOpenWidgetUrl } from '@/shared/lib/getAutoOpenWidgetUrl';
import { usePaymentWidgetModal } from '@/shared/hooks/usePopups';
import { InterpolatedOfferText } from '@/shared/components/InterpolatedOfferText/InterpolatedOfferText';

import { createSubscriptionOfferAnalytics } from '../../lib/subscriptionOfferAnalytics';
import { usePaywallContext } from '../../hooks/usePaywallContext';
import { useI18n } from '../../hooks/useOnboardingI18n';
import { getSubscriptionOfferButtonText } from '../../lib/getSubscriptionOfferButtonText';

import styles from './PaywallSubscriptionInset.module.css';

export const PaywallSubscriptionInset = () => {
    const { offer, paymentPlace } = usePaywallContext();

    if (!offer) {
        return null;
    }

    return <PaywallSubscriptionInsetContent offer={offer} paymentPlace={paymentPlace} />;
};

interface PaywallSubscriptionInsetContentProps {
    offer: Offer;
    paymentPlace?: PaymentPlace;
}

const PaywallSubscriptionInsetContent = ({
    offer,
    paymentPlace,
}: PaywallSubscriptionInsetContentProps) => {
    const { isAuthorized } = useAuthManager();
    const i18n = useI18n();

    const buttonText = getSubscriptionOfferButtonText(
        i18n,
        offer.offerTexts.buttonText,
        !isAuthorized
    );

    const offerAnalytics = useAnalytics((eventSender) =>
        createSubscriptionOfferAnalytics(eventSender, {
            buttonText,
            offer,
        })
    );

    const { refetch: subscriptionRefetch } = useSubscriptionRefetch();
    const offerLoginUrl = useLoginUrl(getAutoOpenWidgetUrl(offer));
    const offerWebAnalytics = useOfferWebAnalytics(offer);

    const paymentWidget = usePaymentWidgetModal();

    const controller = useMemo(() => {
        function onPurchaseSuccess(data: TarifficatorPurchaseSuccessWidgetEvent) {
            subscriptionRefetch();
            offerAnalytics.succeeded();
            offerWebAnalytics.purchaseSucceeded(data);
        }
        return {
            onOfferShow: () => {
                offerAnalytics.showed();
                offerWebAnalytics.offerShowed();
                paymentPlace?.analytics.offerShowed(offer);
            },
            onOfferClick: () => {
                offerAnalytics.navigated();
                offerWebAnalytics.offerClicked();
                paymentPlace?.analytics.offerClicked(offer);

                paymentWidget.open(paymentPlace, offer, {
                    onPurchaseSuccess,
                    onNeedAuth: () => window.location.assign(offerLoginUrl),
                });
            },
        };
    }, [
        offer,
        offerLoginUrl,
        offerWebAnalytics,
        paymentWidget,
        paymentPlace,
        offerAnalytics,
        subscriptionRefetch,
    ]);

    const buttonTextWithDetails = offer.offerTexts?.buttonTextWithDetails;
    const buttonVariant = isAuthorized ? 'plus' : 'accent';
    const shouldShowAdditionText = Boolean(buttonTextWithDetails && isAuthorized);

    return (
        <div className={styles.root}>
            <Button
                className={styles.button}
                size={60}
                variant={buttonVariant}
                onClick={controller.onOfferClick}
                data-test-id="SubscriptionPurchaseButton"
                onShow={controller.onOfferShow}
            >
                {buttonText}
            </Button>

            {shouldShowAdditionText && (
                <InterpolatedOfferText
                    legals={offer?.catalogCompositeOffer?.legalInfo?.items}
                    text={buttonTextWithDetails}
                    className={styles.purchaseButtonText}
                />
            )}
        </div>
    );
};
